/**
 * Path Graph API (Learning Paths DAG builder — M3).
 *
 * Granular per-gesture persistence endpoints under
 * /api/learning-paths/{pathId} (LearningPathVoter::EDIT). Every mutation
 * carries expectedUpdatedAt and returns the path's NEW updatedAt; a stale
 * token yields 409 {error:'stale', updatedAt}.
 */

import axiosInstance from "../axiosInstance";
import type {
  AddItemToStepRequest,
  AddItemToStepResponse,
  CreateGraphConnectionRequest,
  CreateGraphConnectionResponse,
  CreateStepRequest,
  CreateStepResponse,
  DeleteGraphConnectionRequest,
  DeleteGraphConnectionResponse,
  DeleteGraphItemRequest,
  DeleteGraphItemResponse,
  DeleteStepRequest,
  DeleteStepResponse,
  DuplicatePathRequest,
  DuplicatePathResponse,
  GraphResponse,
  MoveGraphItemRequest,
  MoveGraphItemResponse,
  ReorderStepItemsRequest,
  ReorderStepItemsResponse,
  UpdateGraphConnectionRequest,
  UpdateGraphConnectionResponse,
  UpdateGraphItemRequest,
  UpdateGraphItemResponse,
  UpdateStepPositionsRequest,
  UpdateStepPositionsResponse,
  UpdateStepRequest,
  UpdateStepResponse,
} from "./types";

/** GET /api/learning-paths/{pathId}/graph */
export const getPathGraph = async (pathId: number): Promise<GraphResponse> => {
  const response = await axiosInstance.get<GraphResponse>(
    `/api/learning-paths/${pathId}/graph`,
  );
  return response.data;
};

/** POST /api/learning-paths/{pathId}/steps */
export const createStep = async (
  pathId: number,
  data: CreateStepRequest,
): Promise<CreateStepResponse> => {
  const response = await axiosInstance.post<CreateStepResponse>(
    `/api/learning-paths/${pathId}/steps`,
    data,
  );
  return response.data;
};

/** PATCH /api/learning-paths/{pathId}/steps/{stepId} */
export const updateStep = async (
  pathId: number,
  stepId: number,
  data: UpdateStepRequest,
): Promise<UpdateStepResponse> => {
  const response = await axiosInstance.patch<UpdateStepResponse>(
    `/api/learning-paths/${pathId}/steps/${stepId}`,
    data,
  );
  return response.data;
};

/** PATCH /api/learning-paths/{pathId}/steps/positions (batch) */
export const updateStepPositions = async (
  pathId: number,
  data: UpdateStepPositionsRequest,
): Promise<UpdateStepPositionsResponse> => {
  const response = await axiosInstance.patch<UpdateStepPositionsResponse>(
    `/api/learning-paths/${pathId}/steps/positions`,
    data,
  );
  return response.data;
};

/** DELETE /api/learning-paths/{pathId}/steps/{stepId} (body carries token) */
export const deleteStep = async (
  pathId: number,
  stepId: number,
  data: DeleteStepRequest,
): Promise<DeleteStepResponse> => {
  const response = await axiosInstance.delete<DeleteStepResponse>(
    `/api/learning-paths/${pathId}/steps/${stepId}`,
    { data },
  );
  return response.data;
};

/** POST /api/learning-paths/{pathId}/steps/{stepId}/items (M4: all 8 types) */
export const addItemToStep = async (
  pathId: number,
  stepId: number,
  data: AddItemToStepRequest,
): Promise<AddItemToStepResponse> => {
  const response = await axiosInstance.post<AddItemToStepResponse>(
    `/api/learning-paths/${pathId}/steps/${stepId}/items`,
    data,
  );
  return response.data;
};

/** PATCH /api/learning-paths/{pathId}/items/{itemId} (generic per-type update) */
export const updateGraphItem = async (
  pathId: number,
  itemId: number,
  data: UpdateGraphItemRequest,
): Promise<UpdateGraphItemResponse> => {
  const response = await axiosInstance.patch<UpdateGraphItemResponse>(
    `/api/learning-paths/${pathId}/items/${itemId}`,
    data,
  );
  return response.data;
};

/** DELETE /api/learning-paths/{pathId}/items/{itemId} (body carries token) */
export const deleteGraphItem = async (
  pathId: number,
  itemId: number,
  data: DeleteGraphItemRequest,
): Promise<DeleteGraphItemResponse> => {
  const response = await axiosInstance.delete<DeleteGraphItemResponse>(
    `/api/learning-paths/${pathId}/items/${itemId}`,
    { data },
  );
  return response.data;
};

/** POST /api/learning-paths/{pathId}/items/{itemId}/move */
export const moveGraphItem = async (
  pathId: number,
  itemId: number,
  data: MoveGraphItemRequest,
): Promise<MoveGraphItemResponse> => {
  const response = await axiosInstance.post<MoveGraphItemResponse>(
    `/api/learning-paths/${pathId}/items/${itemId}/move`,
    data,
  );
  return response.data;
};

/** PUT /api/learning-paths/{pathId}/steps/{stepId}/items/reorder */
export const reorderStepItems = async (
  pathId: number,
  stepId: number,
  data: ReorderStepItemsRequest,
): Promise<ReorderStepItemsResponse> => {
  const response = await axiosInstance.put<ReorderStepItemsResponse>(
    `/api/learning-paths/${pathId}/steps/${stepId}/items/reorder`,
    data,
  );
  return response.data;
};

/** POST /api/learning-paths/{pathId}/connections */
export const createGraphConnection = async (
  pathId: number,
  data: CreateGraphConnectionRequest,
): Promise<CreateGraphConnectionResponse> => {
  const response = await axiosInstance.post<CreateGraphConnectionResponse>(
    `/api/learning-paths/${pathId}/connections`,
    data,
  );
  return response.data;
};

/** PATCH /api/learning-paths/{pathId}/connections/{connectionId} */
export const updateGraphConnection = async (
  pathId: number,
  connectionId: number,
  data: UpdateGraphConnectionRequest,
): Promise<UpdateGraphConnectionResponse> => {
  const response = await axiosInstance.patch<UpdateGraphConnectionResponse>(
    `/api/learning-paths/${pathId}/connections/${connectionId}`,
    data,
  );
  return response.data;
};

/** DELETE /api/learning-paths/{pathId}/connections/{connectionId} (body carries token) */
export const deleteGraphConnection = async (
  pathId: number,
  connectionId: number,
  data: DeleteGraphConnectionRequest,
): Promise<DeleteGraphConnectionResponse> => {
  const response = await axiosInstance.delete<DeleteGraphConnectionResponse>(
    `/api/learning-paths/${pathId}/connections/${connectionId}`,
    { data },
  );
  return response.data;
};

/** POST /api/learning-paths/{pathId}/duplicate (deep copy → 201, always draft) */
export const duplicateLearningPath = async (
  pathId: number,
  data: DuplicatePathRequest,
): Promise<DuplicatePathResponse> => {
  const response = await axiosInstance.post<DuplicatePathResponse>(
    `/api/learning-paths/${pathId}/duplicate`,
    data,
  );
  return response.data;
};
